// NavPanel.tsx — Atrium tier-2 context panel (Sprint 82, ADR-082).
// ----------------------------------------------------------------------------
// 236px panel: section title + description, "Quick create" primary button, the
// vertical sub-page list (active = white card + shadow + indigo text), and an
// optional gradient progress card at the bottom. Visual spec mirrors prototype
// `.panel` (theme2.jsx ~34-54, nav2.jsx NavPanel).
//
// Sub-pages come straight from the active section's menuConfig items; selecting
// one calls the SAME `selectAndReveal` handler the legacy sidebar uses, so the
// content area and selected-page state stay shared between both shells.
import React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import type { AtriumSection } from './navModel';
import type { DashboardComponent } from '../dashboard/menuConfig';

export interface NavPanelProgressCard {
  title:    string;
  subtitle: string;
  pct:      number;
}

export interface NavPanelProps {
  section:        AtriumSection | undefined;
  selected:       DashboardComponent;
  onSelect:       (key: DashboardComponent) => void;
  onQuickCreate?: () => void;
  progressCard?:  NavPanelProgressCard;
}

const panelStyle: React.CSSProperties = {
  width: 'var(--panel-w)', flex: 'none', background: 'var(--panel)',
  borderRight: '1px solid var(--line)', display: 'flex', flexDirection: 'column',
};

const createBtnStyle: React.CSSProperties = {
  margin: '4px 16px 12px', height: 40, borderRadius: 11,
  background: 'var(--blue)', color: '#fff', fontFamily: 'inherit',
  fontSize: 13.5, fontWeight: 700, border: 0, display: 'flex',
  alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer',
  boxShadow: 'var(--shc)',
};

function itemStyle(active: boolean): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 11, padding: '9px 11px',
    borderRadius: 10, fontSize: 13.5, marginBottom: 2, cursor: 'pointer',
    position: 'relative', width: '100%', textAlign: 'left',
    border: 0, fontFamily: 'inherit',
    fontWeight: active ? 700 : 600,
    color: active ? 'var(--blue)' : 'var(--ink2)',
    background: active ? 'var(--card)' : 'transparent',
    boxShadow: active ? 'var(--sh)' : 'none',
  };
}

export const NavPanel: React.FC<NavPanelProps> = ({
  section, selected, onSelect, onQuickCreate, progressCard,
}) => {
  if (!section) return <nav style={panelStyle} aria-label="Section navigation" />;

  return (
    <nav style={panelStyle} aria-label={`${section.label} navigation`}>
      <div style={{ padding: '22px 20px 14px' }}>
        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.4px' }}>
          {section.label}
        </div>
        <div style={{ fontSize: 12, color: 'var(--mut)', marginTop: 3, fontWeight: 500 }}>
          {section.desc}
        </div>
      </div>

      {onQuickCreate && (
        <button type="button" style={createBtnStyle} onClick={onQuickCreate}>
          <PlusOutlined style={{ fontSize: 16 }} />
          Quick create
        </button>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '6px 12px' }}>
        {section.items.map(item => {
          const active = selected === item.key;
          return (
            <button
              key={item.key}
              type="button"
              aria-current={active ? 'page' : undefined}
              style={itemStyle(active)}
              onClick={() => onSelect(item.key)}
            >
              <span style={{ display: 'flex', color: active ? 'var(--blue)' : 'var(--mut)' }}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>
      {/* close .panel scroll list */}

      {progressCard && (
        <div style={{
          margin: '12px 16px',
          background: 'linear-gradient(160deg,#2563EB,#4F7BF2)',
          borderRadius: 14, padding: 15, color: '#fff',
        }}>
          <div style={{ fontSize: 13, fontWeight: 800 }}>{progressCard.title}</div>
          <div style={{ fontSize: 11.5, opacity: .9, marginTop: 3, lineHeight: 1.4 }}>
            {progressCard.subtitle}
          </div>
          <div style={{
            height: 6, borderRadius: 5, background: 'rgba(255,255,255,.28)',
            marginTop: 10, overflow: 'hidden',
          }}>
            <i style={{
              display: 'block', height: '100%', background: '#fff',
              borderRadius: 5, width: `${Math.max(0, Math.min(100, progressCard.pct))}%`,
            }} />
          </div>
        </div>
      )}
    </nav>
  );
};
